import { authGuard, permissionGuard } from '@abp/ng.core';
import { NgModule } from '@angular/core';
import { RouterModule, Routes } from '@angular/router';

import { IDENTITY_ENTITY_ACTION_CONTRIBUTORS } from './identity/identity-entity-action.contributors';

const routes: Routes = [
  {
    path: '',
    pathMatch: 'full',
    redirectTo: 'requested-claims',
  },
  {
    path: 'new-claim',
    title: '::NewRequestedClaim',
    loadChildren: () =>
      import('./new-claim/onboarding.module').then(m => m.OnboardingModule),
  },
  {
    // Public: a guest follows a claim with its code (#64).
    path: 'track-claim',
    loadComponent: () =>
      import('./standalone/landing-layout/landing-layout.component').then(m => m.LandingLayoutComponent),
    children: [
      {
        path: '',
        title: '::GuestTrackTitle',
        loadComponent: () => import('./track-claim/track-claim.component').then(m => m.TrackClaimComponent),
      },
    ],
  },
  {
    // Public: the claim form (#72) and tracking (#73) inside a client's site. No site header or
    // footer, the client's page is around them.
    path: 'embed/:key',
    loadComponent: () => import('./embed/embed.component').then(m => m.EmbedComponent),
    children: [
      {
        path: '',
        title: '::NewRequestedClaim',
        loadComponent: () =>
          import('./new-claim/onboarding-wizard/onboarding-wizard.component').then(m => m.OnboardingWizardComponent),
      },
      {
        path: 'track',
        title: '::GuestTrackTitle',
        loadComponent: () => import('./track-claim/track-claim.component').then(m => m.TrackClaimComponent),
      },
    ],
  },
  {
    path: 'dashboard',
    title: '::Menu:Dashboard',
    loadChildren: () => import('./dashboard/dashboard.module').then(m => m.DashboardModule),
    canActivate: [authGuard, permissionGuard],
  },
  {
    path: 'account',
    loadChildren: () => import('@abp/ng.account').then(m => m.AccountModule.forLazy()),
  },
  {
    path: 'identity',
    loadChildren: () =>
      import('@abp/ng.identity').then(m =>
        m.IdentityModule.forLazy({
          entityActionContributors: IDENTITY_ENTITY_ACTION_CONTRIBUTORS,
        })
      ),
  },
  {
    path: 'security-logs',
    title: '::Menu:SecurityLogs',
    loadChildren: () =>
      import('./security-logs/security-log.module').then(m => m.SecurityLogModule),
  },
  {
    path: 'referrers',
    title: '::Menu:Referrers',
    loadChildren: () => import('./referrers/referrer.module').then(m => m.ReferrerModule),
  },
  {
    path: 'partner-leads',
    title: '::Menu:PartnerLeads',
    loadChildren: () => import('./partner-leads/partner-lead.module').then(m => m.PartnerLeadModule),
  },
  {
    path: 'reports',
    title: '::Menu:Reports',
    loadChildren: () => import('./reports/report.module').then(m => m.ReportModule),
  },
  {
    path: 'setting-management',
    loadChildren: () =>
      import('@abp/ng.setting-management').then(m => m.SettingManagementModule.forLazy()),
  },
  {
    path: 'gdpr-cookie-consent',
    loadChildren: () =>
      import('./gdpr-cookie-consent/gdpr-cookie-consent.module').then(
        m => m.GdprCookieConsentModule
      ),
  },
  {
    path: 'cities',
    title: '::Menu:Cities',
    loadChildren: () => import('./cities/city/city.module').then(m => m.CityModule),
  },
  {
    path: 'categories',
    title: '::Menu:Categories',
    loadChildren: () => import('./categories/category/category.module').then(m => m.CategoryModule),
  },
  {
    path: 'sub-categories',
    title: '::Menu:SubCategories',
    loadChildren: () =>
      import('./sub-categories/sub-category/sub-category.module').then(m => m.SubCategoryModule),
  },
  {
    path: 'places',
    title: '::Menu:Places',
    loadChildren: () => import('./places/place/place.module').then(m => m.PlaceModule),
  },
  {
    path: 'business-types',
    title: '::Menu:BusinessTypes',
    loadChildren: () =>
      import('./business-types/business-type/business-type.module').then(m => m.BusinessTypeModule),
  },
  {
    path: 'requested-items',
    title: '::Menu:RequestedItems',
    loadChildren: () =>
      import('./requested-items/requested-item/requested-item.module').then(
        m => m.RequestedItemModule
      ),
  },
  {
    path: 'stored-items',
    title: '::Menu:StoredItems',
    loadChildren: () =>
      import('./stored-items/stored-item/stored-item.module').then(m => m.StoredItemModule),
  },
  {
    path: 'partners',
    title: '::Menu:Partners',
    loadChildren: () => import('./partners/partner/partner.module').then(m => m.PartnerModule),
  },
  {
    path: 'admins',
    title: '::Menu:Admins',
    loadChildren: () => import('./admins/admin/admin.module').then(m => m.AdminModule),
  },
  {
    path: 'users',
    title: '::Menu:Users',
    loadChildren: () => import('./users/user/user.module').then(m => m.UserModule),
  },
  {
    path: 'branches',
    title: '::Menu:Branches',
    loadChildren: () => import('./branches/branch/branch.module').then(m => m.BranchModule),
  },
  { path: 'fees', title: '::Menu:Fees', loadChildren: () => import('./fees/fee/fee.module').then(m => m.FeeModule) },
  {
    path: 'requested-claims',
    title: '::Menu:RequestedClaims',
    loadChildren: () =>
      import('./requested-claims/requested-claim/requested-claim.module').then(
        m => m.RequestedClaimModule
      ),
  },
  {
    path: 'stored-item/:storedItemId',
    title: '::Menu:StoredItemDetails',
    loadChildren: () =>
      import('./stored-items-details/stored-items-details.module').then(
        m => m.StoredItemDetailsModule
      ),
  },
  {
    path: 'shipments',
    title: '::Menu:Shipments',
    loadChildren: () => import('./shipments/shipment/shipment.module').then(m => m.ShipmentModule),
  },
  {
    path: 'frequently-asked-questions',
    title: '::Menu:FrequentlyAskedQuestions',
    loadChildren: () =>
      import(
        './frequently-asked-questions/frequently-asked-question/frequently-asked-question.module'
      ).then(m => m.FrequentlyAskedQuestionModule),
  },
  {
    path: 'matches',
    title: '::Menu:Matches',
    loadChildren: () => import('./matches/match/match.module').then(m => m.MatchModule),
  },
  {
    path: 'help-center',
    title: '::Menu:HelpCenter',
    loadChildren: () => import('./help-center-view/help-center-view.module').then(m => m.HelpCenterViewModule),
  },
   { path: 'topics',
    title: '::Menu:Topics',
    loadChildren: () => import('./topics/topic/topic.module').then(m => m.TopicModule),
  },
];

@NgModule({
  imports: [RouterModule.forRoot(routes, {})],
  exports: [RouterModule],
})
export class AppRoutingModule {}
